import { useState } from 'react'
import type { RoomSnapshot } from '../../lib/api/rooms'
import type { RoomPlayerRow } from '../../lib/database.types'
import { castCourseVote } from '../../lib/api/rooms'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'

interface Props {
  snapshot: RoomSnapshot
  me: RoomPlayerRow
}

const COURSE_NAMES = ['Starter', 'Main', 'Dessert'] as const
const COURSE_EMOJIS = ['🥗', '🍽️', '🍮']

/**
 * Presenting phase on the phone. Branches on who the active presenter is:
 *
 *  - If me → "You're up" screen with my course text for cue-card reference
 *  - If someone else → "I'd eat that" / "Hard pass" vote buttons
 *
 * Once I vote, the buttons flip to a read-only confirmation so I can't
 * double-vote — the server enforces uniqueness anyway, but this avoids the
 * confusing round-trip error.
 */
export function JoinPresentingPhase({ snapshot, me }: Props) {
  const t = useT()
  const { room, players, submissions, courseVotes } = snapshot
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The server's presenter cursor indexes the NON-skipped seats only
  // (advance_phase counts `where not skipped`), so filter before
  // indexing — same as LiveRoomScreen. Indexing the raw list made
  // guests vote on the wrong player's dish after any mid-game skip.
  const activePlayers = players.filter(p => !p.skipped)
  const presenter = activePlayers[room.current_player_index] ?? activePlayers[0]
  const isMyTurn = presenter?.id === me.id
  const presenterSub = presenter
    ? submissions.find(s => s.player_id === presenter.id) ?? null
    : null

  const courseIdx = (room.active_course - 1) as 0 | 1 | 2
  const courseName = t(COURSE_NAMES[courseIdx])
  const courseEmoji = COURSE_EMOJIS[courseIdx]

  const courseText = !presenterSub ? '' :
    courseIdx === 0 ? presenterSub.course1_text :
    courseIdx === 1 ? presenterSub.course2_text :
                      presenterSub.course3_text

  // Have I already voted for (this submission, this course)?
  const myVote = presenterSub
    ? courseVotes.find(cv =>
        cv.submission_id === presenterSub.id &&
        cv.course_number === room.active_course &&
        cv.voter_id === me.id)
    : undefined

  async function handleVote(wouldEat: boolean) {
    if (busy) return
    if (!presenterSub) {
      // Defensive: if the presenter's submission hasn't propagated yet
      // (AI plating mid-flight, or a stale realtime tick), tell the
      // user explicitly instead of swallowing the click.
      setError(t("This presenter hasn't plated their course yet. Give it a moment, then try again."))
      return
    }
    setBusy(true); setError(null)
    try {
      await castCourseVote(
        presenterSub.id,
        room.active_course as 1 | 2 | 3,
        wouldEat,
      )
      // Realtime tick will backfill `myVote`. Keep busy until it lands
      // — but reset busy after a 2.5s safety so a missed tick doesn't
      // leave the buttons disabled forever.
      setTimeout(() => setBusy(false), 2500)
    } catch (e) {
      setError(readError(e))
      setBusy(false)
    }
  }

  // ── Presenter view ─────────────────────────────────────────────────────
  if (isMyTurn) {
    return (
      <div className="flex-1 flex flex-col px-5 py-8">
        <div className="text-center mb-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-iet-green mb-1">{t("You're presenting!")}</p>
          <h1 className="font-display text-3xl font-bold text-brand-text">
            {courseEmoji} Your {courseName}
          </h1>
          <p className="text-brand-muted text-sm mt-1">{t('Read it aloud. The TV has it too.')}</p>
        </div>

        <div className="card-lg p-6 bg-white mb-6">
          {courseText && courseText.trim() ? (
            <p className="text-brand-text text-lg leading-relaxed">"{courseText}"</p>
          ) : (
            <p className="text-brand-muted text-center">
              (You didn't write a story for this course.)
            </p>
          )}
        </div>

        <div className="card p-4 bg-brand-elevated mt-auto">
          <p className="text-xs font-semibold text-brand-text mb-1">{t('How this works')}</p>
          <ul className="text-xs text-brand-muted space-y-1">
            <li>• Pitch it like a waiter on a good night.</li>
            <li>• Everyone else votes: would they eat it or not?</li>
            <li>• When you're done, the TV moves on to the next presenter.</li>
          </ul>
        </div>
      </div>
    )
  }

  // ── Voter view ─────────────────────────────────────────────────────────
  return (
    <div className="flex-1 flex flex-col px-5 py-8">
      <div className="text-center mb-6">
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted mb-1">
          {courseEmoji} Course {room.active_course} · {courseName}
        </p>
        <h1 className="font-display text-3xl font-bold text-brand-text">
          {t('{name} is presenting', { name: presenter?.display_name ?? t('Someone') })}
        </h1>
        <p className="text-brand-muted text-sm mt-1">{t('Listen to their pitch, then vote.')}</p>
      </div>

      {/* Show the text as a reference — small, subdued; the TV is the star. */}
      <div className="card p-4 bg-white mb-8">
        {courseText && courseText.trim() ? (
          <p className="text-brand-text text-sm leading-relaxed">"{courseText}"</p>
        ) : presenter?.is_ai && !presenterSub ? (
          <p className="text-brand-muted text-sm text-center">
            🤖 {presenter.display_name} is plating this course… hang on.
          </p>
        ) : (
          <p className="text-brand-muted text-sm text-center">
            (No story written.)
          </p>
        )}
      </div>

      {myVote ? (
        <div className="card-lg p-6 text-center mb-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted mb-2">{t('Your vote')}</p>
          <p className="font-display text-3xl font-bold"
             style={{ color: myVote.would_eat ? '#4A8B3B' : '#C62828' }}>
            {myVote.would_eat ? t("I'd eat that ✓") : t('Hard pass ✗')}
          </p>
          <p className="text-sm text-brand-muted mt-3">{t('Waiting for the TV to move on…')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 mb-6">
          <button
            onClick={() => handleVote(true)}
            disabled={busy}
            className="py-6 rounded-2xl bg-iet-green text-white
                       font-display text-2xl font-bold
                       shadow-lg active:scale-95 transition-all
                       disabled:opacity-40"
          >
            {busy ? t('Sending vote…') : "👍 I'd eat that"}
          </button>
          <button
            onClick={() => handleVote(false)}
            disabled={busy}
            className="py-6 rounded-2xl bg-brand-danger text-white
                       font-display text-2xl font-bold
                       shadow-lg active:scale-95 transition-all
                       disabled:opacity-40"
          >
            {busy ? t('Sending vote…') : '👎 Hard pass'}
          </button>
          {busy && (
            <p className="text-center text-xs text-brand-muted mt-1">{t('If this hangs, tap again. Buttons auto-reset after a couple seconds.')}</p>
          )}
        </div>
      )}

      {error && (
        <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
          <p className="text-sm text-brand-danger">{error}</p>
        </div>
      )}
    </div>
  )
}
